import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';
import { formatTime, formatDate } from '../../utils/timezone.js';
import { validateEmail, validateRequired } from '@yebo/shared';
import { availabilityReasonHeadline, NO_AVAILABILITY_DEFAULT } from './availability-reason-copy.js';

/**
 * R10.4 / R10.6 / R10.9 — Empty-state DEMAND RECOVERY.
 *
 * Replaces the dead-end "No times available for this date." string. When a date
 * comes back with no bookable slots, this block offers, in priority order:
 *
 *   1. Alternate same-day times          (R10.4)
 *   2. Alternate online sections         (R10.4)
 *   3. "Try another date" → next open    (R10.4)
 *   4. Join Notify (cancellation list)   (R10.6)
 *   5. A sibling-venue cross-sell link   (R10.9)
 *
 * Everything is driven by the optionally-enriched availability payload — when
 * the backend has not yet shipped enrichment (View 5 · R5.1) the alternates are
 * empty and the block degrades to "try another date / Join Notify / sibling
 * venue", never a dead end. The component owns no booking state; it emits typed
 * events the host maps onto the store.
 *
 * Rendered ONLY when the V2 flag is on and the date is genuinely empty — gated
 * by the parent (`yebo-step-datetime`), so the fast path is untouched when off.
 */
@customElement('yebo-step-datetime-recovery')
export class YeboStepDatetimeRecovery extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    css`
      :host { display: block; }
      .recovery {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-md);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg-elevated);
        padding: var(--yebo-space-md);
      }
      .recovery-head {
        display: flex;
        align-items: center;
        gap: var(--yebo-space-sm);
      }
      .recovery-icon {
        flex-shrink: 0;
        font-size: 1.1em;
        color: var(--yebo-primary);
      }
      .recovery-title {
        font-weight: 600;
        color: var(--yebo-text-heading);
        font-size: var(--yebo-font-size-base);
      }
      .recovery-sub {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .group-label {
        font-weight: 500;
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        margin-bottom: var(--yebo-space-xs);
      }
      .alt-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
        gap: var(--yebo-space-xs);
      }
      .alt-btn {
        padding: 6px;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg);
        cursor: pointer;
        font-size: var(--yebo-font-size-sm);
        font-family: var(--yebo-font);
        color: var(--yebo-text);
        text-align: center;
        transition: all var(--yebo-transition);
      }
      .alt-btn:hover {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .alt-btn .alt-section {
        display: block;
        font-size: 11.2px;
        color: var(--yebo-text-muted);
      }
      .section-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--yebo-space-sm);
        padding: 8px 10px;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg);
      }
      .section-name { font-size: var(--yebo-font-size-sm); color: var(--yebo-text); }
      .section-next { font-size: var(--yebo-font-size-sm); color: var(--yebo-text-muted); }
      .link-btn {
        background: none;
        border: none;
        color: var(--yebo-primary);
        cursor: pointer;
        font-family: var(--yebo-font);
        font-size: var(--yebo-font-size-sm);
        text-decoration: underline;
        padding: 0;
        text-align: left;
      }
      .try-date {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--yebo-space-sm);
        flex-wrap: wrap;
      }
      .divider {
        height: 1px;
        background: var(--yebo-border);
        margin: var(--yebo-space-xs) 0;
      }
      /* Notify form */
      .notify-form { display: flex; flex-direction: column; gap: var(--yebo-space-sm); }
      .notify-row { display: grid; grid-template-columns: 1fr 1fr; gap: var(--yebo-space-sm); }
      @media (max-width: 400px) { .notify-row { grid-template-columns: 1fr; } }
      .notify-success {
        display: flex;
        align-items: center;
        gap: var(--yebo-space-sm);
        color: var(--yebo-success, #2ea043);
        font-size: var(--yebo-font-size-sm);
        font-weight: 500;
      }
      .sibling {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .sibling a { color: var(--yebo-primary); text-decoration: underline; }
      @media (pointer: coarse) {
        .alt-btn, .link-btn { min-height: 44px; }
      }
    `,
  ];

  @property({ type: String }) timezone = 'UTC';
  @property({ type: String, attribute: 'selected-date' }) selectedDate = '';
  @property({ type: String, attribute: 'availability-reason' }) availabilityReason = '';
  @property({ type: Array }) alternateTimes: Array<{ start: string; end: string; available: boolean; sectionLabel?: string }> = [];
  @property({ type: Array }) alternateSections: Array<{ sectionId: string; sectionLabel: string; nextAvailable?: string }> = [];
  @property({ type: String, attribute: 'next-available-date' }) nextAvailableDate = '';
  @property({ attribute: false }) siblingVenue: { name: string; url: string } | null = null;

  // Prefill from the contact step if the guest already typed it.
  @property({ type: String, attribute: 'first-name' }) firstName = '';
  @property({ type: String, attribute: 'last-name' }) lastName = '';
  @property({ type: String }) email = '';
  @property({ type: String }) phone = '';

  @property({ type: Boolean, attribute: 'notify-submitting' }) notifySubmitting = false;
  @property({ type: Boolean, attribute: 'notify-submitted' }) notifySubmitted = false;
  @property({ type: String, attribute: 'notify-error' }) notifyError = '';

  // Local notify-form working copy (committed on submit).
  @state() private _nFirst = '';
  @state() private _nEmail = '';
  @state() private _nFormError = '';
  @state() private _notifyOpen = false;

  private get _firstValue(): string { return this._nFirst || this.firstName; }
  private get _emailValue(): string { return this._nEmail || this.email; }

  // #521 follow-up — the copy now lives in availability-reason-copy.ts so this
  // gated block and the ungated empty state in step-datetime.ts cannot drift.
  // This component is not given the party size, so the size-agnostic phrasing is
  // the one it renders.
  private _reasonHeadline(): string {
    return availabilityReasonHeadline(this.availabilityReason) ?? NO_AVAILABILITY_DEFAULT;
  }

  private _selectAlternate(start: string, end: string) {
    this.dispatchEvent(new CustomEvent('alternate-time-selected', {
      detail: { start, end }, bubbles: true, composed: true,
    }));
  }

  private _tryDate(date: string) {
    if (!date) return;
    this.dispatchEvent(new CustomEvent('try-another-date', {
      detail: { date }, bubbles: true, composed: true,
    }));
  }

  private _toggleNotify() {
    this._notifyOpen = !this._notifyOpen;
  }

  private _submitNotify() {
    const first = this._firstValue.trim();
    const email = this._emailValue.trim();
    if (!validateRequired(first)) { this._nFormError = 'Please enter your name'; return; }
    if (!validateRequired(email) || !validateEmail(email)) { this._nFormError = 'Enter a valid email'; return; }
    this._nFormError = '';
    this.dispatchEvent(new CustomEvent('join-notify', {
      detail: { firstName: first, lastName: this.lastName, email, phone: this.phone },
      bubbles: true, composed: true,
    }));
  }

  render() {
    const hasAltTimes = this.alternateTimes.some((s) => s.available);
    const hasAltSections = this.alternateSections.length > 0;

    return html`
      <div class="recovery" aria-live="polite">
        <div class="recovery-head">
          <span class="recovery-icon" aria-hidden="true">&#9888;</span>
          <span class="recovery-title">${this._reasonHeadline()}</span>
        </div>
        <p class="recovery-sub">Here are a few ways to still get a table.</p>

        ${hasAltTimes ? html`
          <div>
            <p class="group-label">Other times that day</p>
            <div class="alt-grid" role="listbox" aria-label="Alternate times">
              ${this.alternateTimes.filter((s) => s.available).map((slot) => html`
                <button
                  class="alt-btn"
                  role="option"
                  aria-selected="false"
                  @click=${() => this._selectAlternate(slot.start, slot.end)}
                >
                  ${formatTime(slot.start, this.timezone)}
                  ${slot.sectionLabel ? html`<span class="alt-section">${slot.sectionLabel}</span>` : nothing}
                </button>
              `)}
            </div>
          </div>
        ` : nothing}

        ${hasAltSections ? html`
          <div>
            <p class="group-label">Try another seating area</p>
            ${this.alternateSections.map((sec) => html`
              <div class="section-row">
                <span class="section-name">${sec.sectionLabel}</span>
                ${sec.nextAvailable ? html`
                  <button
                    class="link-btn"
                    @click=${() => this._selectAlternate(sec.nextAvailable!, sec.nextAvailable!)}
                  >Next: ${formatTime(sec.nextAvailable, this.timezone)}</button>
                ` : html`<span class="section-next">Available</span>`}
              </div>
            `)}
          </div>
        ` : nothing}

        ${this.nextAvailableDate ? html`
          <div class="try-date">
            <span class="section-next">Next available: ${formatDate(`${this.nextAvailableDate}T12:00:00`, this.timezone)}</span>
            <button class="btn btn-secondary" @click=${() => this._tryDate(this.nextAvailableDate)}>Try this date</button>
          </div>
        ` : html`
          <p class="recovery-sub">Pick another date on the calendar above to see more times.</p>
        `}

        <div class="divider"></div>

        <!-- R10.6 — Join Notify -->
        ${this.notifySubmitted ? html`
          <div class="notify-success">
            <span aria-hidden="true">&#10003;</span>
            <span>You're on the list. We'll text or email you the moment a table opens up.</span>
          </div>
        ` : html`
          <div>
            <p class="group-label">Want us to let you know if something frees up?</p>
            ${this._notifyOpen ? html`
              <div class="notify-form">
                <div class="notify-row">
                  <input
                    class="input"
                    type="text"
                    aria-label="First name"
                    placeholder="First name"
                    .value=${this._firstValue}
                    @input=${(e: Event) => { this._nFirst = (e.target as HTMLInputElement).value; }}
                  />
                  <input
                    class="input"
                    type="email"
                    aria-label="Email"
                    placeholder="Email"
                    .value=${this._emailValue}
                    @input=${(e: Event) => { this._nEmail = (e.target as HTMLInputElement).value; }}
                  />
                </div>
                ${this._nFormError ? html`<span class="field-error" role="alert">${this._nFormError}</span>` : nothing}
                ${this.notifyError ? html`<span class="field-error" role="alert">${this.notifyError}</span>` : nothing}
                <button
                  class="btn btn-primary"
                  ?disabled=${this.notifySubmitting}
                  @click=${this._submitNotify}
                >${this.notifySubmitting ? 'Joining…' : 'Join the notify list'}</button>
              </div>
            ` : html`
              <button class="btn btn-secondary" @click=${this._toggleNotify}>Notify me of openings</button>
            `}
          </div>
        `}

        <!-- R10.9 — sibling-venue cross-sell -->
        ${this.siblingVenue ? html`
          <p class="sibling">
            Or try our sister venue:
            <a href=${this.siblingVenue.url} target="_blank" rel="noopener noreferrer">${this.siblingVenue.name}</a>
          </p>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-datetime-recovery': YeboStepDatetimeRecovery;
  }
}
